import { useRef, useCallback, useEffect } from "react";
import { calculateDropPosition, canDrop, finalizeDrop, removeDraggedItemFromLayout, snapToGrid } from "./dragUtils";
import { getGuidelineData } from "../Guidelines/guidelinesFunc";
import {
  adjustSectionHeights,
  findItemById,
  getPositionedElementConfig,
  getValueByPath,
  setValueByPath,
  updateBlockInLayout,
  updateOnlyElementConfigInLayout
} from "../utils";
import useSingleAndDoubleClick from "../../../hooks/useSingleAndDoubleClick";

export const useDragMeta = (containerRef, screenWidth) => {
  const dropDataRef = useRef(null);
  const canDropCacheRef = useRef({});
  const dragMetaRef = useRef(null);
  const cacheCleanupTimerRef = useRef(null);

  const itemRemovedRef = useRef(false);
  const originalItemRef = useRef(null);

  const generateDragData = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    dragMetaRef.current = {
      fullScreenContainerRect: rect,
      correctionFactor: screenWidth / rect.width
    };
  }, [containerRef, screenWidth]);

  const getDragMeta = useCallback(() => {
    if (!dragMetaRef.current) {
      generateDragData();
    }
    return dragMetaRef.current;
  }, [generateDragData]);

  const checkCanDrop = useCallback((draggedItem, targetItem) => {
    const dropKey = `${draggedItem.id}-${targetItem.id}`;
    if (canDropCacheRef.current[dropKey] === undefined) {
      canDropCacheRef.current[dropKey] = canDrop(draggedItem, targetItem);
    }
    return canDropCacheRef.current[dropKey];
  }, []);

  const clearCache = useCallback(() => {
    canDropCacheRef.current = {};
  }, []);

  const scheduleCacheCleanup = useCallback(() => {
    if (cacheCleanupTimerRef.current) {
      clearTimeout(cacheCleanupTimerRef.current);
    }
    cacheCleanupTimerRef.current = setTimeout(clearCache, 5000);
  }, [clearCache]);

  const resetDragMeta = useCallback(() => {
    dropDataRef.current = null;
    dragMetaRef.current = null;
    itemRemovedRef.current = false;
    originalItemRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (cacheCleanupTimerRef.current) {
        clearTimeout(cacheCleanupTimerRef.current);
      }
      clearCache();
    };
  }, [clearCache]);

  return {
    dropDataRef,
    dragMetaRef,
    generateDragData,
    getDragMeta,
    checkCanDrop,
    clearCache,
    scheduleCacheCleanup,
    resetDragMeta,
    itemRemovedRef,
    originalItemRef
  };
};

export const useDrag = ({
  isEditable,
  draggedItem,
  dragOffset,
  layoutConfig,
  setLayoutConfig,
  selectedItem,
  setSelectedItem,
  setDraggedItem,
  setDragOffset,
  setHoveredItemId,
  setGuidlineData,
  setMultiSelectedItems,
  closeEditBar,
  gridSize,
  scaleFactor,
  containerRef,
  dragMeta
}) => {
  const {
    dropDataRef,
    generateDragData,
    getDragMeta,
    checkCanDrop,
    resetDragMeta,
    scheduleCacheCleanup,
    itemRemovedRef,
    originalItemRef
  } = dragMeta;

  const lastDragOverTimeRef = useRef(0);
  const THROTTLE_MS = 16; // ~60fps

  const createDragImage = useCallback(
    (e, offsetX, offsetY) => {
      const clone = e.target.cloneNode(true);
      clone.style.cssText = `
      overflow: hidden;
      width: ${e.target.clientWidth}px;
      height: ${e.target.clientHeight}px;
      position: absolute;
      top: -9999px;
      left: -9999px;
      zoom: ${scaleFactor};
    `;
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
      setTimeout(() => document.body.removeChild(clone), 0);
    },
    [scaleFactor]
  );

  const handleDragStart = useCallback(
    (e, item) => {
      if (e.shiftKey || !isEditable()) return;

      e.stopPropagation();

      try {
        originalItemRef.current = {
          item: JSON.parse(JSON.stringify(item)),
          layoutConfig: JSON.parse(JSON.stringify(layoutConfig))
        };
      } catch (error) {
        console.warn("Failed to backup item:", error);
        originalItemRef.current = { item, layoutConfig };
      }

      itemRemovedRef.current = false;
      lastDragOverTimeRef.current = 0;

      const rect = e.target.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;

      generateDragData();
      setDragOffset({ x: offsetX, y: offsetY });
      setDraggedItem(item);
      setMultiSelectedItems(null);

      if (selectedItem?.id === item.id) {
        setSelectedItem(null);
        closeEditBar();
      }

      createDragImage(e, offsetX, offsetY);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditable,
      generateDragData,
      setDragOffset,
      setDraggedItem,
      setMultiSelectedItems,
      selectedItem,
      setSelectedItem,
      closeEditBar,
      createDragImage,
      layoutConfig
    ]
  );

  const calculateAdjustedDropPosition = useCallback((initialX, initialY, itemType) => {
    let dropX = initialX;
    let dropY = initialY;

    if (itemType === "section") {
      dropX = dropY = "0px";
    } else if (itemType === "column") {
      dropY = "0px";
    }

    return { dropX, dropY };
  }, []);

  const determineParentId = useCallback((targetItem, draggedItem, layoutConfig) => {
    if (targetItem === "outer" && draggedItem.type !== "section" && layoutConfig.length) {
      return layoutConfig[layoutConfig.length - 1].id;
    }
    return targetItem.id;
  }, []);

  const handleDragOver = useCallback(
    (e, targetItem, parentId, otherInfo) => {
      if (!isEditable() || !draggedItem || !targetItem) return;

      e.preventDefault();
      e.stopPropagation();

      const now = Date.now();
      if (now - lastDragOverTimeRef.current < THROTTLE_MS) {
        return;
      }
      lastDragOverTimeRef.current = now;

      const dropData = dropDataRef.current;
      if (!dropData?.dragId && !itemRemovedRef.current) {
        try {
          removeDraggedItemFromLayout(layoutConfig, draggedItem, otherInfo, setLayoutConfig);
          itemRemovedRef.current = true;
        } catch (error) {
          console.error("Error removing item during drag:", error);
          if (originalItemRef.current) {
            setLayoutConfig(originalItemRef.current.layoutConfig);
          }
          return;
        }
      }

      if (!checkCanDrop(draggedItem, targetItem)) return;

      setHoveredItemId(targetItem.id);

      const meta = getDragMeta();
      if (!meta) return;

      const { fullScreenContainerRect, correctionFactor } = meta;
      const targetContainerRect = e.currentTarget.getBoundingClientRect();

      const { dropX: initialX, dropY: initialY } = calculateDropPosition(
        e,
        targetContainerRect,
        dragOffset,
        correctionFactor,
        gridSize
      );

      const { dropX, dropY } = calculateAdjustedDropPosition(initialX, initialY, draggedItem.type);

      const guideline =
        draggedItem.type === "section"
          ? null
          : getGuidelineData(e, fullScreenContainerRect, dragOffset, draggedItem, correctionFactor, gridSize);

      setGuidlineData(guideline);

      dropDataRef.current = {
        parentId: determineParentId(targetItem, draggedItem, layoutConfig),
        dropX,
        dropY,
        dragId: draggedItem.id,
        otherInfo
      };

      if (selectedItem?.id === draggedItem.id) {
        setSelectedItem(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditable,
      draggedItem,
      layoutConfig,
      setLayoutConfig,
      checkCanDrop,
      setHoveredItemId,
      getDragMeta,
      dragOffset,
      gridSize,
      setGuidlineData,
      selectedItem,
      setSelectedItem,
      calculateAdjustedDropPosition,
      determineParentId,
      dropDataRef
    ]
  );

  const handleOuterSectionDrop = useCallback((draggedItem, layoutConfig, setLayoutConfig) => {
    const newSection = getPositionedElementConfig("section");
    if (!newSection) return;

    newSection.style = draggedItem.style || {};
    setLayoutConfig([...layoutConfig, newSection]);
  }, []);

  const cleanupAfterDrop = useCallback(() => {
    setHoveredItemId(null);
    setDraggedItem(null);
    resetDragMeta();
    scheduleCacheCleanup();
  }, [setHoveredItemId, setDraggedItem, resetDragMeta, scheduleCacheCleanup]);

  const handleOnDrop = useCallback(
    (e, node) => {
      if (!isEditable()) return;

      e.stopPropagation();
      setGuidlineData(null);

      if (!draggedItem?.type) return;

      try {
        if (node === "outer" && draggedItem.type === "section") {
          handleOuterSectionDrop(draggedItem, layoutConfig, setLayoutConfig);
        } else {
          const dropData = dropDataRef.current;
          if (!dropData?.parentId) {
            console.warn("Drop failed: no parent ID found");
            if (originalItemRef.current) {
              setLayoutConfig(originalItemRef.current.layoutConfig);
            }
            return;
          }
          finalizeDrop(dropData, draggedItem, layoutConfig, setLayoutConfig);
        }
      } catch (error) {
        console.error("Error during drop:", error);
        if (originalItemRef.current) {
          setLayoutConfig(originalItemRef.current.layoutConfig);
        }
      } finally {
        cleanupAfterDrop();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEditable,
      setGuidlineData,
      draggedItem,
      layoutConfig,
      setLayoutConfig,
      handleOuterSectionDrop,
      cleanupAfterDrop,
      dropDataRef
    ]
  );

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && draggedItem) {
        // Restore original layout when user cancels drag
        if (originalItemRef.current) {
          setLayoutConfig(originalItemRef.current.layoutConfig);
        }
        setDraggedItem(null);
        setGuidlineData(null);
        setHoveredItemId(null);
        resetDragMeta();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedItem, setLayoutConfig, setDraggedItem, setGuidlineData, setHoveredItemId, resetDragMeta]);

  useEffect(() => {
    if (!isEditable()) return;

    const handleGlobalDrop = (e) => {
      const dropTarget = e.target;
      const container = containerRef.current;
      const isOutsideContainer = container && !container.contains(dropTarget);

      if (isOutsideContainer && draggedItem && dropDataRef.current) {
        handleOnDrop(e);
      }
    };

    const handleGlobalDragOver = (e) => {
      e.preventDefault();
    };

    window.addEventListener("drop", handleGlobalDrop);
    window.addEventListener("dragover", handleGlobalDragOver);

    return () => {
      window.removeEventListener("drop", handleGlobalDrop);
      window.removeEventListener("dragover", handleGlobalDragOver);
    };
  }, [draggedItem, layoutConfig, isEditable, containerRef, handleOnDrop, dropDataRef]);

  return {
    handleDragStart,
    handleDragOver,
    handleOnDrop
  };
};

export const useResize = ({ isEditable, setLayoutConfig, setGuidlineData, containerRef, screenWidth, gridSize }) => {
  const initializeResizeState = useCallback(
    (e, block) => {
      const containerRect = e.target.getBoundingClientRect();
      const initialWidth = parseInt(block.style?.width || "100", 10);
      const initialHeight = parseInt(block.style?.height || "100", 10);
      const initialX = e.clientX;
      const initialY = e.clientY;

      const { left, top, width } = containerRef.current.getBoundingClientRect();
      const correctionFactor = screenWidth / width;

      return {
        containerRect,
        initialWidth,
        initialHeight,
        initialX,
        initialY,
        containerLeft: left,
        containerTop: top,
        correctionFactor
      };
    },
    [containerRef, screenWidth]
  );

  const updateGuidelinesForResize = useCallback(
    (resizeState, newWidth, newHeight) => {
      const { containerRect, containerLeft, containerTop, initialWidth, initialHeight, correctionFactor } = resizeState;

      setGuidlineData({
        left: correctionFactor * (containerRect.left - containerLeft) - initialWidth + 10,
        top: correctionFactor * (containerRect.top - containerTop) - initialHeight + 10,
        right: correctionFactor * (containerRect.left - containerLeft) - initialWidth + newWidth + 10,
        bottom: correctionFactor * (containerRect.top - containerTop) - initialHeight + newHeight + 10
      });
    },
    [setGuidlineData]
  );

  const updateLayoutForResize = useCallback(
    (updatedBlock, otherInfo) => {
      if (otherInfo?.path) {
        setLayoutConfig((prev) => {
          const main = findItemById(prev || [], otherInfo.path[0]);
          const arrayElement = getValueByPath(main, otherInfo.path[1]);
          const newLayout = updateBlockInLayout(arrayElement, updatedBlock);
          const updated = setValueByPath(main, otherInfo.path[1], newLayout);
          return updateOnlyElementConfigInLayout(prev, otherInfo.path[0], updated.config);
        });
      } else {
        setLayoutConfig((prev) => updateBlockInLayout(prev, updatedBlock));
      }
    },
    [setLayoutConfig]
  );

  const createResizeMouseMoveHandler = useCallback(
    (resizeState, block, otherInfo) => {
      return (moveEvent) => {
        const deltaX = moveEvent.clientX - resizeState.initialX;
        const deltaY = moveEvent.clientY - resizeState.initialY;

        let newWidth = snapToGrid(resizeState.initialWidth + resizeState.correctionFactor * deltaX, gridSize);
        let newHeight = snapToGrid(resizeState.initialHeight + resizeState.correctionFactor * deltaY, gridSize);

        if (block.type === "section") newWidth = "100%";
        if (block.type === "column") newHeight = "100%";

        const updatedBlock = {
          ...block,
          style: {
            ...block.style,
            width: `${newWidth}px`,
            height: `${newHeight}px`
          }
        };

        updateGuidelinesForResize(resizeState, newWidth, newHeight);
        updateLayoutForResize(updatedBlock, otherInfo);
      };
    },
    [gridSize, updateGuidelinesForResize, updateLayoutForResize]
  );

  const createResizeMouseUpHandler = useCallback(
    (otherInfo, onMouseMove) => {
      const onMouseUp = () => {
        setGuidlineData(null);

        if (otherInfo?.path) {
          setLayoutConfig((prev) => {
            const main = findItemById(prev || [], otherInfo.path[0]);
            const arrayElement = getValueByPath(main, otherInfo.path[1]);
            const adjusted = adjustSectionHeights(arrayElement);
            const updated = setValueByPath(main, otherInfo.path[1], adjusted);
            return updateOnlyElementConfigInLayout(prev, otherInfo.path[0], updated.config);
          });
        } else {
          setLayoutConfig((prev) => adjustSectionHeights(prev));
        }

        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
      };

      return onMouseUp;
    },
    [setGuidlineData, setLayoutConfig]
  );

  const handleResizeStart = useCallback(
    (e, block, parentId, otherInfo) => {
      if (!isEditable()) return;

      e.preventDefault();
      e.stopPropagation();

      const resizeState = initializeResizeState(e, block);
      const onMouseMove = createResizeMouseMoveHandler(resizeState, block, otherInfo);
      const onMouseUp = createResizeMouseUpHandler(otherInfo, onMouseMove);

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [isEditable, initializeResizeState, createResizeMouseMoveHandler, createResizeMouseUpHandler]
  );

  return { handleResizeStart };
};

export const useSelection = ({
  isEditable,
  type,
  setSelectedItem,
  setSelectedItemPath,
  setMultiSelectedItems,
  closeEditBar,
  openEditBar
}) => {
  const handleShiftClick = useCallback(
    (item, parentId, otherInfo) => {
      setMultiSelectedItems((prev) => {
        const updated = { ...(prev || {}) };
        if (updated[item.id]) {
          delete updated[item.id];
        } else {
          updated[item.id] = { item, otherInfo, parentId };
        }
        return Object.keys(updated).length ? updated : null;
      });
    },
    [setMultiSelectedItems]
  );

  const handleNormalClick = useCallback(
    (item, otherInfo) => {
      closeEditBar();
      setMultiSelectedItems(null);
      setSelectedItem(item);
      setSelectedItemPath(otherInfo?.path || null);
    },
    [closeEditBar, setMultiSelectedItems, setSelectedItem, setSelectedItemPath]
  );

  const handleSingleClick = useCallback(
    (e, item, parentId, otherInfo) => {
      e.stopPropagation();

      if (type === "template_selector") {
        setSelectedItem(item);
        return;
      }

      if (!isEditable()) return;

      if (e.shiftKey) {
        handleShiftClick(item, parentId, otherInfo);
        closeEditBar();
        setSelectedItem(null);
        setSelectedItemPath(null);
        return;
      }

      handleNormalClick(item, otherInfo);
    },
    [type, isEditable, handleShiftClick, handleNormalClick, closeEditBar, setSelectedItem, setSelectedItemPath]
  );

  const handleDoubleClick = useCallback(
    (e, item, parentId, otherInfo) => {
      e.stopPropagation();
      if (!isEditable()) return;

      openEditBar(item, otherInfo);
      setMultiSelectedItems(null);
      setSelectedItem(item);
      setSelectedItemPath(otherInfo?.path || null);
    },
    [isEditable, openEditBar, setMultiSelectedItems, setSelectedItem, setSelectedItemPath]
  );

  const handleClick = useSingleAndDoubleClick(handleSingleClick, handleDoubleClick, 200);

  return { handleClick };
};

export const useContextMenu = ({
  closeEditBar,
  setMultiSelectedItems,
  setSelectedItem,
  setSelectedItemPath,
  setContextMenu
}) => {
  const handleRightClick = useCallback(
    (e, item, parentId, otherInfo) => {
      e.preventDefault();
      e.stopPropagation();

      closeEditBar();
      setMultiSelectedItems(null);
      setSelectedItem(item);
      setSelectedItemPath(otherInfo?.path || null);

      setContextMenu({
        anchorPoint: { x: e.clientX, y: e.clientY },
        item,
        parentId,
        otherInfo
      });
    },
    [closeEditBar, setMultiSelectedItems, setSelectedItem, setSelectedItemPath, setContextMenu]
  );

  return { handleRightClick };
};
